import {
  CdkFixedSizeVirtualScroll,
  CdkVirtualForOf,
  CdkVirtualScrollViewport,
} from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  model,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { SearchInputComponent } from '@copado/cds';
import { Organization } from '@crt/platform-types';
import { trackByProperty } from '@crt/sui';
import { NgbActiveModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, take } from 'rxjs/operators';

import { UpdateEvent, UserState } from '@app/auth/user.state';
import { BootstrapSuffix } from '@app/common.model';
import { ProjectState } from '@app/projects/project.state';
import { ORG_ID_PARAM, PROJECT_ID_PARAM } from '@app/shared/app-router.service';
import { SharedService } from '@app/shared/shared.service';
import { ToastService } from '@app/toasts/toast.service';

interface SearchableOrg extends Organization {
  search: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    CdkVirtualScrollViewport,
    NgbTooltip,
    ReactiveFormsModule,
    SearchInputComponent,
  ],
  selector: 'app-org-switcher-list',
  templateUrl: './org-switcher-list.component.html',
})
export class OrgSwitcherListComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly projectState = inject(ProjectState);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  protected readonly activeModal = inject(NgbActiveModal);
  protected readonly sharedService = inject(SharedService);
  protected readonly userState = inject(UserState);

  /**
   * Controls whether to show only organizations that the current user can access
   * with their current login provider. When true, filters out organizations where
   * the user's current provider is not in the allowedLoginProviders list.
   *
   * @default false - Shows all organizations the user has a role in.
   */
  public readonly showOnlyAccessibleOrgs = model<boolean>(false);

  protected readonly trackById = trackByProperty('id');
  protected readonly search = new UntypedFormControl('');
  private readonly searchTerm = toSignal(this.search.valueChanges.pipe(debounceTime(300)));

  protected readonly orgsToDisplay = computed(() => {
    const currentUser = this.userState.user();
    if (!currentUser) throw new Error('Missing user information');

    const currentProvider = currentUser.provider;
    const showOnlyAccessibleOrgs = this.showOnlyAccessibleOrgs();

    const orgs =
      showOnlyAccessibleOrgs && currentProvider
        ? this.userState
            .orgs()
            .filter(
              (o) =>
                o.allowedLoginProviders.length === 0 ||
                o.allowedLoginProviders.includes(currentProvider),
            )
        : /** Else displal all the orgs the user has access to. */
          this.userState.orgs();

    return orgs
      .filter((o) =>
        // Display all if the user is superadmin
        currentUser.superAdmin
          ? true
          : // Else display only the orgs that the user has roles in.
            currentUser.role.some((r) => r.organizationId === o.id),
      )
      .map((o) => ({ ...o, search: o.name.toLowerCase() }));
  });

  protected filteredOrgs = computed(() => {
    const term = (this.searchTerm() ?? '').toLocaleLowerCase();
    const orgs = this.orgsToDisplay();

    return term ? orgs.filter((o) => this.searchBy(o, term.toLocaleLowerCase())) : orgs;
  });

  /* Flag to know if `user.defaultOrgId` is set or not. */
  protected defaultOrgNotSet = computed(() => !this.userState.user()?.defaultOrgId);

  searchBy(org: SearchableOrg, term: string): boolean {
    return org.search.includes(term) || org.id.toString().includes(term);
  }

  setDefaultOrg(id: number): void {
    this.userState.updateDefaultOrg(
      id,
      () => {
        this.toastService.displayToast(
          BootstrapSuffix.Success,
          $localize`Your default organization has been set.`,
        );

        if (this.defaultOrgNotSet()) {
          this.switchOrg(id, false);
        }
        this.cdr.detectChanges();
      },
      () => {
        this.toastService.displayToast(
          BootstrapSuffix.Danger,
          $localize`Unable to set the default organization.`,
          $localize`Error`,
          10000,
        );
      },
    );
  }

  switchOrg(org: number | Organization, navigate = true): void {
    if (navigate) {
      const orgId = typeof org === 'number' ? org : org.id;
      this.router.navigate(['/orgs', 'settings'], {
        queryParams: {
          [ORG_ID_PARAM]: orgId,
          [PROJECT_ID_PARAM]: this.projectState.getDefaultProject(orgId) ?? '',
        },
        queryParamsHandling: '',
      });
    } else {
      this.userState.switchOrg$.next(org);
      this.search.setValue('');
    }
    this.activeModal.dismiss();
    this.activeModal.close();
  }

  deleteDefaultOrgId(): void {
    if (this.userState.user()?.settingsJson.defaultOrg) {
      this.userState.updateUser$.next({
        private: {
          settings: {
            defaultOrg: undefined,
          },
        },
      });
      this.userState.userUpdateEvent$.pipe(take(1)).subscribe((event) => {
        if (event === UpdateEvent.Success) {
          this.toastService.displayToast(
            BootstrapSuffix.Success,
            $localize`Your default organization has been deleted.`,
          );

          this.activeModal.dismiss();
          window.location.reload();
        } else {
          this.toastService.displayToast(
            BootstrapSuffix.Danger,
            $localize`Unable delete the default organization.`,
            $localize`Error`,
            10000,
          );
        }
      });
    }
  }
}
